'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  repostSocialPost,
  unrepostSocialPost,
} from '@/src/actions/social-posts';
import { patchInfinitePosts } from '@/src/utils/patchInfinitePosts';

type Vars = {
  postId: number;
  currentlyReposted: boolean;
};

export function useToggleRepostMutation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ postId, currentlyReposted }: Vars) => {
      return currentlyReposted
        ? unrepostSocialPost(postId)
        : repostSocialPost(postId);
    },

    onMutate: async ({ postId, currentlyReposted }) => {
      const queries = qc.getQueryCache().findAll({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          ['profilePosts', 'discoverPosts', 'followedPosts'].includes(
            q.queryKey[0] as string
          ),
      });

      const snapshots = queries.map((q) => ({
        key: q.queryKey,
        data: qc.getQueryData(q.queryKey),
      }));

      await Promise.all(
        queries.map((q) => qc.cancelQueries({ queryKey: q.queryKey }))
      );

      const nextIsReposted = !currentlyReposted;
      const delta = nextIsReposted ? 1 : -1;

      queries.forEach((q) => {
        qc.setQueryData(q.queryKey, (data: any) =>
          patchInfinitePosts(data, postId, (p) => ({
            ...p,
            isReposted: nextIsReposted,
            repostsCount: Math.max(0, (p.repostsCount ?? 0) + delta),
          }))
        );
      });

      return { snapshots };
    },

    onError: (_err, _vars, ctx) => {
      ctx?.snapshots?.forEach(({ key, data }) => qc.setQueryData(key, data));
    },

    onSettled: () => {
      qc.invalidateQueries({
        predicate: (q) =>
          Array.isArray(q.queryKey) &&
          ['profilePosts', 'discoverPosts', 'followedPosts'].includes(
            q.queryKey[0] as string
          ),
      });
    },
  });
}
